import React, {useEffect, useState, useCallback, useRef} from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Image,
  Alert,
  RefreshControl,
  Modal,
  Dimensions,
  PanResponder,
  Animated,
} from 'react-native';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {RootStackParamList} from '../App';
import {Colors} from '../theme/colors';
import {API_CONFIG} from '../config/api';
import {Header} from '../components/Header';

type Props = NativeStackScreenProps<RootStackParamList, 'Pagelle'>;

interface Player {
  id: number;
  name: string;
  image: string | null;
  number: number;
  average: number;
  votes_count: number;
  user_vote: number | null;
}

interface Section {
  title: string;
  players: Player[];
}

interface MatchInfo {
  id: number;
  date: string;
  home_team: {
    name: string;
    logo: string;
    score: number;
  };
  away_team: {
    name: string;
    logo: string;
    score: number;
  };
  result: string | null;
}

interface PagelleResponse {
  success: boolean;
  match: MatchInfo;
  is_open: boolean;
  has_voted: boolean;
  sections: Section[];
}

const RATING_OPTIONS = Array.from({length: 19}, (_, i) => (i + 2) / 2); // 1, 1.5, ... 10
const {width: SCREEN_WIDTH} = Dimensions.get('window');

const VANOLI_IMAGE = 'https://cdn.laviola.it/laviola/players/Vanoli.png';

const getPlayerImage = (player: Player) => {
  const name = player.name.toLowerCase();
  if (name.includes('vanoli')) return VANOLI_IMAGE;
  return player.image || 'https://www.laviola.it/images/player-placeholder.png';
};

const DAYS_IT = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
const MONTHS_IT = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

const formatMatchDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = DAYS_IT[d.getDay()];
  const month = MONTHS_IT[d.getMonth()];
  const hours = d.getHours().toString().padStart(2, '0');
  const mins = d.getMinutes().toString().padStart(2, '0');
  return `${day} ${d.getDate()} ${month} ${d.getFullYear()} alle ${hours}:${mins}`;
};

export const PagelleScreen: React.FC<Props> = ({route, navigation}) => {
  const {matchId} = route.params;
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState<PagelleResponse | null>(null);
  const [votes, setVotes] = useState<{[key: string]: number}>({});
  const [submitting, setSubmitting] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [modalVote, setModalVote] = useState<number | null>(null);
  const modalTranslateY = useRef(new Animated.Value(0)).current;

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: (_, gestureState) => gestureState.dy > 5,
      onPanResponderMove: (_, gestureState) => {
        if (gestureState.dy > 0) {
          modalTranslateY.setValue(gestureState.dy);
        }
      },
      onPanResponderRelease: (_, gestureState) => {
        if (gestureState.dy > 100) {
          Animated.timing(modalTranslateY, {
            toValue: 500,
            duration: 200,
            useNativeDriver: true,
          }).start(() => {
            setModalVisible(false);
            setSelectedPlayer(null);
            modalTranslateY.setValue(0);
          });
        } else {
          Animated.spring(modalTranslateY, {
            toValue: 0,
            useNativeDriver: true,
          }).start();
        }
      },
    })
  ).current;

  const fetchPagelle = useCallback(async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/match/${matchId}/pagelle`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      const json = await response.json();
      if (json.success) {
        setData(json);
        if (json.has_voted) {
          const initialVotes: {[key: string]: number} = {};
          json.sections.forEach((section: Section) => {
            section.players.forEach((p: Player) => {
              if (p.user_vote) initialVotes[p.id] = p.user_vote;
            });
          });
          setVotes(initialVotes);
        }
      } else {
        Alert.alert('Errore', json.message || 'Errore nel caricamento delle pagelle');
      }
    } catch (error) {
      console.error(error);
      Alert.alert('Errore', 'Impossibile caricare i dati');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [matchId]);

  useEffect(() => {
    fetchPagelle();
  }, [fetchPagelle]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchPagelle();
  };

  const openVoteModal = (player: Player) => {
    if (data?.has_voted || !data?.is_open) return;
    setSelectedPlayer(player);
    setModalVote(votes[player.id] ?? null);
    setModalVisible(true);
  };

  const confirmModalVote = () => {
    if (selectedPlayer && modalVote != null) {
      setVotes(prev => ({...prev, [selectedPlayer.id]: modalVote}));
    }
    setModalVisible(false);
    setSelectedPlayer(null);
  };

  const submitVotes = async () => {
    if (submitting || !data) return;
    
    setSubmitting(true);
    try {
      // Build votes payload with string keys
      const votesPayload: {[key: string]: number} = {};
      Object.entries(votes).forEach(([playerId, vote]) => {
        votesPayload[String(playerId)] = vote;
      });

      const response = await fetch(`${API_CONFIG.BASE_URL}/polls/confirm-batch/${data.match.id}`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({votes: votesPayload}),
      });
      
      const json = await response.json();
      
      if (json.success) {
        Alert.alert('Successo', 'Voti inviati con successo!', [
          {text: 'OK', onPress: () => fetchPagelle()}
        ]);
      } else {
         if (json.error === 'missing_votes') {
             Alert.alert('Votazione incompleta', json.message);
         } else if (json.error === 'already_voted') {
             Alert.alert('Già votato', 'Hai già votato per questa partita.');
         } else {
             const msg = json.message || '';
             const italianMsg = msg.includes('votes field is required') 
               ? 'Devi votare tutti i giocatori prima di confermare.'
               : msg || 'Errore durante l\'invio';
             Alert.alert('Errore', italianMsg);
         }
      }
    } catch (_) {
      Alert.alert('Errore', 'Si è verificato un errore di rete');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={Colors.primary} />
      </View>
    );
  }

  if (!data) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>Nessun dato disponibile</Text>
      </View>
    );
  }

  const isReadOnly = data.has_voted || !data.is_open;

  const getScoreColor = (score: number) => {
    if (score < 5) return '#e74c3c';
    if (score < 6) return '#f39c12';
    return '#27ae60';
  };

  return (
    <View style={styles.container}>
      <Header
        showActionBar
        onBackPress={() => navigation.goBack()}
      />

      <ScrollView 
        contentContainerStyle={styles.scrollContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={Colors.primary} />}
      >
        {/* Match Card */}
        <View style={styles.matchCard}>
          <View style={styles.teamRow}>
            <View style={styles.teamInfo}>
                {data.match.home_team.logo && <Image source={{uri: data.match.home_team.logo}} style={styles.logo} />}
                <Text style={styles.teamName}>{data.match.home_team.name}</Text>
            </View>
            <View style={styles.scoreBox}>
                <Text style={styles.scoreText}>
                    {data.match.home_team.score} - {data.match.away_team.score}
                </Text>
            </View>
             <View style={styles.teamInfo}>
                {data.match.away_team.logo && <Image source={{uri: data.match.away_team.logo}} style={styles.logo} />}
                <Text style={styles.teamName}>{data.match.away_team.name}</Text>
            </View>
          </View>
          <Text style={styles.matchDate}>{formatMatchDate(data.match.date)}</Text>
        </View>

        {/* Status Alerts */}
        {data.has_voted && (
            <View style={[styles.alert, styles.alertSuccess]}>
                <Text style={styles.alertText}>Hai già votato per questa partita.</Text>
            </View>
        )}
        {!data.is_open && !data.has_voted && (
            <View style={[styles.alert, styles.alertWarning]}>
                <Text style={styles.alertText}>Le votazioni sono chiuse.</Text>
            </View>
        )}

        {/* Player Sections */}
        {data.sections.map((section, sIdx) => (
          <View key={sIdx} style={styles.section}>
            <Text style={styles.sectionTitle}>{section.title}</Text>
            {section.players.map((player) => {
                const currentVote = votes[player.id];
                const avg = player.average;
                const avgColor = getScoreColor(avg);
                
                return (
                    <TouchableOpacity 
                      key={player.id} 
                      style={styles.playerRow}
                      onPress={() => openVoteModal(player)}
                      disabled={isReadOnly}
                      activeOpacity={isReadOnly ? 1 : 0.7}
                    >
                        <View style={styles.playerHeader}>
                            <View style={styles.playerInfo}>
                                <Image 
                                  source={{uri: getPlayerImage(player)}} 
                                  style={styles.playerAvatar} 
                                />
                                <Text style={styles.playerName}>{player.name}</Text>
                            </View>
                            <View style={styles.scoreSection}>
                                {currentVote != null && (
                                  <View style={styles.userVoteBadge}>
                                    <Text style={styles.userVoteText}>{currentVote}</Text>
                                  </View>
                                )}
                                <View style={[styles.avgBadge, {backgroundColor: avgColor + '18'}]}>
                                    <Text style={[styles.avgText, {color: avgColor}]}>{avg.toFixed(1)}</Text>
                                </View>
                            </View>
                        </View>
                        {!isReadOnly && !currentVote && (
                          <View style={styles.tapHint}>
                            <Text style={styles.tapHintText}>Tocca per votare</Text>
                          </View>
                        )}
                    </TouchableOpacity>
                );
            })}
          </View>
        ))}

        <View style={styles.footerSpace} />
      </ScrollView>

      {/* Footer Submit Action */}
      {!isReadOnly && (
        <View style={styles.footer}>
            <TouchableOpacity 
                style={[styles.submitBtn, submitting && styles.submitBtnDisabled]}
                onPress={submitVotes}
                disabled={submitting}
            >
                <Text style={styles.submitBtnText}>
                    {submitting ? 'Invio in corso...' : 'Conferma Voti'}
                </Text>
            </TouchableOpacity>
        </View>
      )}

      {/* Vote Modal */}
      <Modal
        visible={modalVisible}
        transparent
        animationType="slide"
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <TouchableOpacity 
            style={styles.modalDismiss} 
            activeOpacity={1} 
            onPress={() => setModalVisible(false)} 
          />
          <Animated.View style={[styles.modalContent, {transform: [{translateY: modalTranslateY}]}]}>
            {/* Drag Handle */}
            <View {...panResponder.panHandlers} style={styles.modalHandleArea}>
              <View style={styles.modalHandle} />
            </View>

            {/* Modal Header */}
            <View style={styles.modalHeader}>
              <TouchableOpacity onPress={() => setModalVisible(false)} style={styles.modalHeaderBtn}>
                <Text style={styles.modalCancel}>Annulla</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                onPress={confirmModalVote} 
                style={[styles.modalHeaderBtn, styles.modalConfirmBtn, modalVote == null && {opacity: 0.4}]}
                disabled={modalVote == null}
              >
                <Text style={styles.modalConfirmText}>Conferma</Text>
              </TouchableOpacity>
            </View>

            {/* Player Info */}
            {selectedPlayer && (
              <View style={styles.modalPlayerInfo}>
                <Image 
                  source={{uri: getPlayerImage(selectedPlayer)}} 
                  style={styles.modalAvatar} 
                />
                <Text style={styles.modalPlayerName}>{selectedPlayer.name}</Text>
                <View style={styles.modalAvgRow}>
                  <Text style={styles.modalAvgLabel}>Media attuale </Text>
                  <Text style={[styles.modalAvgValue, {color: getScoreColor(selectedPlayer.average)}]}>{selectedPlayer.average.toFixed(1)}</Text>
                </View>
              </View>
            )}

            {/* Current Vote Display */}
            <View style={styles.modalVoteDisplay}>
              <Text style={[styles.modalVoteValue, modalVote != null ? {color: getScoreColor(modalVote)} : {color: '#ddd'}]}>
                {modalVote != null ? modalVote.toFixed(1) : '-'}
              </Text>
              <Text style={styles.modalVoteHint}>
                {modalVote != null ? 'Il tuo voto' : 'Seleziona un voto'}
              </Text>
            </View>

            {/* Score Grid */}
            <View style={styles.sliderGrid}>
              {RATING_OPTIONS.map((val) => {
                const isSelected = modalVote === val;
                const btnColor = getScoreColor(val);
                return (
                  <TouchableOpacity
                    key={val}
                    style={[
                      styles.sliderBtn,
                      isSelected && {backgroundColor: btnColor, borderColor: btnColor},
                    ]}
                    onPress={() => setModalVote(val)}
                  >
                    <Text style={[
                      styles.sliderBtnText,
                      isSelected && styles.sliderBtnTextSelected,
                    ]}>
                      {val % 1 === 0 ? val.toFixed(0) : val.toFixed(1)}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </Animated.View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    textAlign: 'center',
    marginTop: 20,
    color: '#666',
  },
  scrollContent: {
    paddingBottom: 100,
  },
  matchCard: {
    backgroundColor: Colors.primary,
    margin: 16,
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 3,
  },
  teamRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: 12,
  },
  teamInfo: {
    alignItems: 'center',
    flex: 1,
  },
  logo: {
    width: 48,
    height: 48,
    marginBottom: 8,
    resizeMode: 'contain',
  },
  teamName: {
    fontSize: 12,
    textAlign: 'center',
    fontWeight: '600',
    color: '#fff',
  },
  scoreBox: {
    paddingHorizontal: 16,
  },
  scoreText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#fff',
  },
  matchDate: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.7)',
  },
  alert: {
    marginHorizontal: 16,
    marginBottom: 16,
    padding: 12,
    borderRadius: 8,
  },
  alertSuccess: {
    backgroundColor: '#d4edda',
    borderWidth: 1,
    borderColor: '#c3e6cb',
  },
  alertWarning: {
    backgroundColor: '#fff3cd',
    borderWidth: 1,
    borderColor: '#ffeeba',
  },
  alertText: {
    color: '#333',
    textAlign: 'center',
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: Colors.primary,
    marginLeft: 16,
    marginBottom: 12,
  },
  playerRow: {
    backgroundColor: '#fff',
    marginBottom: 1,
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  playerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  playerInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  playerAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 12,
    backgroundColor: '#eee',
  },
  playerName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    flex: 1,
  },
  scoreSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  userVoteBadge: {
    backgroundColor: Colors.primary,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  userVoteText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: 'bold',
  },
  avgBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  avgText: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  tapHint: {
    marginTop: 0,
    paddingLeft: 52,
  },
  tapHintText: {
    fontSize: 12,
    color: '#aaa',
    fontStyle: 'italic',
  },
  footerSpace: {
    height: 80,
  },
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#eee',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: -2},
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 10,
  },
  submitBtn: {
    backgroundColor: Colors.primary,
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  submitBtnDisabled: {
    opacity: 0.7,
  },
  submitBtnText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },

  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  modalDismiss: {
    flex: 1,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    paddingBottom: 40,
    paddingHorizontal: 20,
    paddingTop: 12,
  },
  modalHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#ddd',
  },
  modalHandleArea: {
    alignItems: 'center',
    paddingVertical: 8,
    marginBottom: 8,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalHeaderBtn: {
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  modalCancel: {
    fontSize: 15,
    color: '#999',
  },
  modalConfirmBtn: {
    backgroundColor: Colors.primary,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 20,
  },
  modalConfirmText: {
    fontSize: 15,
    color: '#fff',
    fontWeight: 'bold',
  },
  modalPlayerInfo: {
    alignItems: 'center',
    marginBottom: 16,
  },
  modalAvatar: {
    width: 72,
    height: 72,
    borderRadius: 36,
    marginBottom: 12,
    backgroundColor: '#eee',
    borderWidth: 3,
    borderColor: Colors.primary + '30',
  },
  modalPlayerName: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#222',
    marginBottom: 6,
  },
  modalAvgRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  modalAvgLabel: {
    fontSize: 14,
    color: '#999',
  },
  modalAvgValue: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  modalVoteDisplay: {
    alignItems: 'center',
    marginBottom: 24,
  },
  modalVoteValue: {
    fontSize: 56,
    fontWeight: 'bold',
  },
  modalVoteHint: {
    fontSize: 13,
    color: '#bbb',
    marginTop: 4,
  },
  sliderGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 10,
    paddingHorizontal: 4,
    marginBottom: 8,
  },
  sliderBtn: {
    width: (SCREEN_WIDTH - 40 - 10 * 6) / 7,
    height: (SCREEN_WIDTH - 40 - 10 * 6) / 7,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: '#e8e8e8',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8f8f8',
  },
  sliderBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
    textAlign: 'center',
    includeFontPadding: false,
    textAlignVertical: 'center',
  },
  sliderBtnTextSelected: {
    color: '#fff',
    fontWeight: 'bold',
  },
});
